import { makeAutoObservable } from 'mobx'
import { restClient } from '@/Core'
import type { Notify } from '@/Ship/Contracts/Notify'

/**
 * Context passed into `subscribeEvents` so the changeStatus handler
 * can read the current assignment uuid, reload the drawer, and notify the user.
 */
export interface SubscribeContext {
    getAssignmentUuid: () => string
    reload: () => void
    notify: Notify
}

/**
 * Minimal shape of `crudContext.eventManager` used by this store.
 * Mirrors Core's EventManager `on`/`off` signature without pulling the full type in.
 */
interface EventManagerLike {
    on: (event: string, key: string, handler: (...args: any[]) => void) => void
    off: (event: string, key: string) => void
}

/**
 * Local store of `AssignmentViewTemplate`.
 * Owns template-level UI state (modal toggles, selected delay, photo preview,
 * dispatcher-comment edit buffer + saving flag) and async actions
 * (changeStatus, saveComment) plus the EventManager wiring for action buttons.
 * Instantiated per template mount via `useState(() => new AssignmentViewTemplateStore())`.
 */
export class AssignmentViewTemplateStore {
    resolveModalOpen = false
    selectedDelay: any = null
    confirmOpen = false
    pushModalOpen = false
    previewPhotos: string[] = []
    previewOpen = false
    commentEdit: string | null = null
    savedComment: string | undefined = undefined
    savingComment = false
    reportDelayOpen = false
    completeTaskOpen = false

    constructor() {
        makeAutoObservable(this)
    }

    setResolveModalOpen(v: boolean): void {
        this.resolveModalOpen = v
    }

    setSelectedDelay(v: any): void {
        this.selectedDelay = v
    }

    setConfirmOpen(v: boolean): void {
        this.confirmOpen = v
    }

    setPushModalOpen(v: boolean): void {
        this.pushModalOpen = v
    }

    setPreviewPhotos(v: string[]): void {
        this.previewPhotos = v
    }

    setPreviewOpen(v: boolean): void {
        this.previewOpen = v
    }

    setCommentEdit(v: string | null): void {
        this.commentEdit = v
    }

    setSavedComment(v: string | undefined): void {
        this.savedComment = v
    }

    setSavingComment(v: boolean): void {
        this.savingComment = v
    }

    setReportDelayOpen(v: boolean): void {
        this.reportDelayOpen = v
    }

    setCompleteTaskOpen(v: boolean): void {
        this.completeTaskOpen = v
    }

    showPhotos(urls: string[]): void {
        this.setPreviewPhotos(urls)
        this.setPreviewOpen(true)
    }

    openResolveFor(delay: any): void {
        this.setSelectedDelay(delay)
        this.setResolveModalOpen(true)
    }

    async changeStatus(assignmentUuid: string, status: string, notify: Notify): Promise<boolean> {
        if (!assignmentUuid) return false
        try {
            await restClient.post(`/services/executor-assignments/${assignmentUuid}/change-status`, {
                json: { status },
            })
            notify.success('Статус змінено')
            return true
        } catch {
            notify.error('Помилка зміни статусу')
            return false
        }
    }

    async saveComment(assignmentUuid: string, notify: Notify): Promise<boolean> {
        this.setSavingComment(true)
        try {
            await restClient.patch(`/services/executor-assignments/${assignmentUuid}`, {
                json: { dispatcherComment: this.commentEdit },
            })
            notify.success('Коментар збережено')
            this.setSavedComment(this.commentEdit ?? '')
            this.setCommentEdit(null)
            return true
        } catch {
            notify.error('Помилка збереження')
            return false
        } finally {
            this.setSavingComment(false)
        }
    }

    /**
     * Wires the drawer-button events (emitted by `assignmentButtons.ts`) to store actions.
     * Returns a cleanup function suitable for a React `useEffect` return.
     */
    subscribeEvents(em: EventManagerLike | undefined, ctx: SubscribeContext): () => void {
        if (!em) return () => {}

        em.on('assignmentAction:openPushModal', 'openPushModal', () => this.setPushModalOpen(true))
        em.on('assignmentAction:openConfirmModal', 'openConfirmModal', () => this.setConfirmOpen(true))
        em.on('assignmentAction:openReportDelayModal', 'openReportDelayModal', () => this.setReportDelayOpen(true))
        em.on('assignmentAction:openCompleteTaskModal', 'openCompleteTaskModal', () => this.setCompleteTaskOpen(true))
        em.on('assignmentAction:changeStatus', 'changeStatus', async ({ status }: { status: string }) => {
            const ok = await this.changeStatus(ctx.getAssignmentUuid(), status, ctx.notify)
            if (ok) ctx.reload()
        })

        return () => {
            em.off('assignmentAction:openPushModal', 'openPushModal')
            em.off('assignmentAction:openConfirmModal', 'openConfirmModal')
            em.off('assignmentAction:openReportDelayModal', 'openReportDelayModal')
            em.off('assignmentAction:openCompleteTaskModal', 'openCompleteTaskModal')
            em.off('assignmentAction:changeStatus', 'changeStatus')
        }
    }
}
